import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Loader2 } from 'lucide-react';
import {
  useCreateProject,
  useGenerateProjectCode,
  useOrganizationsWithDetails,
  useOrganizationContacts,
  useProjectManagers,
} from '@/hooks/useProjects';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ProjectFormData } from '@/types/project.types';

const projectFormSchema = z.object({
  name: z.string().min(1, 'Project name is required'),
  type: z.string().min(1, 'Project type is required'),
  description: z.string().optional(),
  organization_id: z.string().min(1, 'Organization is required'),
  project_code: z.string().optional(),
  budget: z.number().optional(),
  hourly_rate: z.number().optional(),
  project_manager_id: z.string().optional(),
  client_contact_id: z.string().optional(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  status: z.string().optional(),
  estimated_hours: z.number().optional(),
  overhead_rate: z.number().min(0).max(1).optional(),
  external_sync_enabled: z.boolean().optional(),
});

export default function CreateProject() {
  const navigate = useNavigate();
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');

  const { data: organizations } = useOrganizationsWithDetails();
  const { data: projectManagers } = useProjectManagers();
  const { data: contacts } = useOrganizationContacts(selectedOrganizationId);

  const createProject = useCreateProject();
  const generateCode = useGenerateProjectCode();

  const form = useForm<ProjectFormData>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: {
      status: 'active',
      overhead_rate: 0.15,
      external_sync_enabled: false,
    },
  });

  const onSubmit = async (data: ProjectFormData) => {
    try {
      // Clean and transform the data to match database expectations
      const projectData: ProjectFormData = {
        name: data.name,
        type: data.type,
        organization_id: data.organization_id,
        status: data.status || 'active',
        description: data.description || undefined,
        project_code: data.project_code || undefined,
        budget: data.budget ? Number(data.budget) : undefined,
        hourly_rate: data.hourly_rate ? Number(data.hourly_rate) : undefined,
        project_manager_id: data.project_manager_id || undefined,
        client_contact_id: data.client_contact_id || undefined,
        start_date: data.start_date || undefined,
        end_date: data.end_date || undefined,
        estimated_hours: data.estimated_hours ? Number(data.estimated_hours) : undefined,
        overhead_rate: data.overhead_rate !== undefined ? Number(data.overhead_rate) : 0.15,
        external_sync_enabled: data.external_sync_enabled || false,
        value: data.budget ? Number(data.budget) : undefined, // Use budget as value
      };

      const project = await createProject.mutateAsync(projectData);
      navigate(`/projects/${project.id}`);
    } catch (error) {
      console.error('Project creation error:', error);
      // Error will be handled by the mutation's onError callback
    }
  };

  const handleGenerateCode = async () => {
    if (!selectedOrganizationId) return;

    try {
      const code = await generateCode.mutateAsync(selectedOrganizationId);
      form.setValue('project_code', code);
    } catch (error) {
      // Error handling omitted intentionally
    }
  };

  const handleOrganizationChange = (orgId: string) => {
    setSelectedOrganizationId(orgId);
    form.setValue('organization_id', orgId);
    // Clear client contact when organization changes
    form.setValue('client_contact_id', '');
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/projects">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <h1 className="text-3xl font-bold">Create New Project</h1>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Project Details</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Basic Information</h3>

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Project Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter project name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Project Type</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select project type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="branding">Branding</SelectItem>
                            <SelectItem value="website">Website</SelectItem>
                            <SelectItem value="marketing">Marketing</SelectItem>
                            <SelectItem value="production">Production</SelectItem>
                            <SelectItem value="strategy">Strategy</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Status</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="on_hold">On Hold</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Enter project description"
                          className="min-h-[100px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Organization and Contacts */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Client Information</h3>

                <FormField
                  control={form.control}
                  name="organization_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client Organization</FormLabel>
                      <Select onValueChange={handleOrganizationChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select organization" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {organizations?.map((org) => (
                            <SelectItem key={org.id} value={org.id}>
                              <div className="flex flex-col">
                                <span>{org.name}</span>
                                {org.contact_count > 0 && (
                                  <span className="text-xs text-gray-500">
                                    {org.contact_count} contact{org.contact_count !== 1 ? 's' : ''}
                                    {org.primary_contact_name &&
                                      ` • Primary: ${org.primary_contact_name}`}
                                  </span>
                                )}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="client_contact_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Primary Client Contact</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select client contact" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {contacts?.map((contact) => (
                            <SelectItem key={contact.id} value={contact.id}>
                              <div className="flex flex-col">
                                <span>{contact.full_name}</span>
                                <div className="text-xs text-gray-500 flex gap-2">
                                  {contact.job_title && <span>{contact.job_title}</span>}
                                  {contact.email && <span>{contact.email}</span>}
                                  {contact.is_primary && (
                                    <span className="text-blue-600">Primary</span>
                                  )}
                                </div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="project_manager_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Project Manager</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select project manager" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {projectManagers?.map((manager) => (
                            <SelectItem key={manager.id} value={manager.id}>
                              <div className="flex flex-col">
                                <span>{manager.full_name}</span>
                                <div className="text-xs text-gray-500 flex gap-2">
                                  {manager.job_title && <span>{manager.job_title}</span>}
                                  {manager.email && <span>{manager.email}</span>}
                                </div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Project Code and Budget */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Project Details</h3>

                <FormField
                  control={form.control}
                  name="project_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Project Code</FormLabel>
                      <div className="flex gap-2">
                        <FormControl>
                          <Input placeholder="e.g., ABC-2024-001" {...field} />
                        </FormControl>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleGenerateCode}
                          disabled={!selectedOrganizationId || generateCode.isPending}
                        >
                          {generateCode.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Generate'
                          )}
                        </Button>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="budget"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Budget ($)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="0.00"
                            {...field}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value ? parseFloat(e.target.value) : undefined
                              )
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="hourly_rate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Hourly Rate ($)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="0.00"
                            {...field}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value ? parseFloat(e.target.value) : undefined
                              )
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Enhanced Project Settings */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Advanced Settings</h3>

                <div className="grid gap-4 md:grid-cols-3">
                  <FormField
                    control={form.control}
                    name="estimated_hours"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Estimated Hours</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="0"
                            {...field}
                            onChange={(e) =>
                              field.onChange(e.target.value ? parseInt(e.target.value) : undefined)
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="overhead_rate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Overhead Rate (%)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="15"
                            {...field}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value ? parseFloat(e.target.value) / 100 : undefined
                              )
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="external_sync_enabled"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">External Sync</FormLabel>
                          <div className="text-sm text-muted-foreground">
                            Enable HIVE/Expensify integration
                          </div>
                        </div>
                        <FormControl>
                          <input
                            type="checkbox"
                            checked={field.value || false}
                            onChange={(e) => field.onChange(e.target.checked)}
                            className="h-4 w-4"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Timeline */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Timeline</h3>

                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="start_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Start Date</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="end_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>End Date</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-4">
                <Link to="/projects">
                  <Button variant="outline">Cancel</Button>
                </Link>
                <Button type="submit" disabled={createProject.isPending}>
                  {createProject.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Project'
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
